import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { StoreContext } from '../context/StoreContext';
import { TailSpin } from 'react-loader-spinner';
import { TAVAHOT_PRIVACY_URL, TAVAHOT_TERMS_URL } from '../constants/policies';

const WelcomeWalkinCustomer = () => {
  const { roadId, restaurantId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const tableId = searchParams.get('tableId');
  const qrIdParam = searchParams.get('qrId');
  const promotedDishesParam = searchParams.get('promotedDishes');
  const singlePromotedDishParam = searchParams.get('promotedDish');
  const promotedDishIds = useMemo(() => {
    const parseDishIds = (value) => {
      if (!value) return [];
      return value
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
    };

    const combined = [
      ...parseDishIds(promotedDishesParam),
      ...parseDishIds(singlePromotedDishParam)
    ];

    return Array.from(new Set(combined));
  }, [promotedDishesParam, singlePromotedDishParam]);
  const primaryPromotedDishId = promotedDishIds[0];
  const navigate = useNavigate();
  const { setRoadId, setRequestId, setSource, deviceId, setCartData, setTableId, setQrId, userDetails, userId, accessToken, transientUser } = useContext(StoreContext);
  const [restaurantName, setRestaurantName] = useState('');
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [restaurantOnline, setRestaurantOnline] = useState(false);
  const [tncLoading, setTncLoading] = useState(false);
  const [tncError, setTncError] = useState(null);
  const timerRef = useRef(null);

  // Effect for handling navigation
  useEffect(() => {
    if (shouldRedirect) {
      const promotedQueryValue = promotedDishIds
        .map((dishId) => encodeURIComponent(dishId))
        .join(',');
      const menuPath = promotedDishIds.length > 0
        ? `/menu-items/${restaurantId}?promotedDishes=${promotedQueryValue}`
        : `/menu-items/${restaurantId}`;

      navigate(menuPath, {
        state: {
          name: restaurantName,
          restaurantId: restaurantId,
          roadId: roadId,
          positionGeohash: roadId,
          promotedDishIds,
          promotedDishId: primaryPromotedDishId
        }
      });
    }

    if (qrIdParam) {
      setQrId(qrIdParam);
    } else {
      setQrId(undefined);
    }
  }, [shouldRedirect, restaurantId, restaurantName, roadId, navigate, promotedDishIds, primaryPromotedDishId]);

  useEffect(() => {
    const clearCart = async () => {
      try {
        await axios.post("/api/nexus/cart/clear/device/" + deviceId);
        console.log("Cart cleared on welcome page");
        setCartData({});
      } catch (error) {
        console.error("Error clearing cart:", error);
      }
    };

    const authenticateImplicit = async () => {
      if (!sessionId) {
        setAuthLoading(false);
        return;
      }
      try {
        await userDetails(sessionId);
      } catch (e) {
        console.error("Implicit auth failed:", e);
        // If implicit auth fails with 400, treat it as "logged out" but still allow menu viewing.
        // A popup is shown from StoreContext.userDetails() for this case.
        if (e?.response?.status !== 400) {
          setError("Failed to authenticate. Please try again.");
        }
      } finally {
        setAuthLoading(false);
      }
    };

    // Store roadId in context
    if (roadId) {
      setRoadId(roadId);
    }

    // Store sessionId as requestId
    if (sessionId) {
      setRequestId(sessionId);
      setTableId(tableId); // Store tableId in context
    }

    // Clear cart and fetch restaurant details
    const initializePage = async () => {
      await Promise.all([
        clearCart(),
        fetchRestaurantDetails(),
        authenticateImplicit(),
      ]);
    };

    // Fetch restaurant details
    const fetchRestaurantDetails = async () => {
      try {
        // First, check restaurant operation status
        const statusResponse = await axios.get(`/api/catalogue/restaurant/${roadId}/${restaurantId}`);
        console.log("Restaurant status response:", statusResponse.data);
        
        if (statusResponse.data) {
          const { operationStatus, name, lat, lng } = statusResponse.data;
          
          // Check if restaurant is online
          if (operationStatus !== 'ONLINE') {
            let errorMessage = "Restaurant is currently closed";
            
            // Provide more specific messages based on status
            switch (operationStatus) {
              case 'OFFLINE':
                errorMessage = "Restaurant is currently closed";
                break;
              case 'BUSY':
                errorMessage = "Restaurant is currently busy and not accepting orders";
                break;
              case 'MAINTENANCE':
                errorMessage = "Restaurant is under maintenance";
                break;
              default:
                errorMessage = `Restaurant is currently ${operationStatus.toLowerCase()}`;
            }
            
            setError(errorMessage);
            setLoading(false);
            return;
          }
          
          setRestaurantName(name);
          // Set source using restaurant's position
          if (lat && lng) {
            setSource(`${lat},${lng}`);
          }
          setRestaurantOnline(true);
        }
      } catch (error) {
        console.error("Error fetching restaurant details:", error);
        
        // Provide more specific error messages based on error type
        if (error.response) {
          // Server responded with error status
          if (error.response.status === 404) {
            setError("Restaurant not found");
          } else if (error.response.status === 500) {
            setError("Server error. Please try again later");
          } else {
            setError(`Error: ${error.response.status} - ${error.response.statusText}`);
          }
        } else if (error.request) {
          // Network error
          setError("Network error. Please check your connection and try again");
        } else {
          // Other error
          setError("Failed to load restaurant details");
        }
      } finally {
        setLoading(false);
      }
    };

    if (roadId && restaurantId) {
      initializePage();
    } else {
      setError("Missing restaurant information");
      setLoading(false);
      setAuthLoading(false);
    }
  }, [roadId, restaurantId, sessionId, tableId, qrIdParam, setRequestId, setSource, deviceId, setCartData, setRoadId, setTableId, setQrId]);

  // Start countdown only for non-transient users (once auth is resolved and restaurant is online)
  useEffect(() => {
    if (!restaurantOnline) return;
    if (authLoading) return;
    if (shouldRedirect) return;
    if (transientUser === true) return; // transient users must tap
    if (timerRef.current) return; // already started

    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          setShouldRedirect(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [restaurantOnline, authLoading, transientUser, shouldRedirect]);

  const handleGoToMenu = async () => {
    setTncError(null);

    // If user is transient, accept T&C first
    if (transientUser === true) {
      if (!userId || !accessToken) {
        setTncError("Missing authentication details. Please try again.");
        return;
      }

      setTncLoading(true);
      try {
        const response = await axios.post(
          "/api/userservice/tnc/accept",
          { userId },
          {
            headers: {
              accept: "application/json",
              "Content-Type": "application/json",
              "auth-token": accessToken,
              "user-id": userId,
            },
          }
        );

        if (response?.data?.success) {
          setShouldRedirect(true);
        } else {
          setTncError(
            response?.data?.message ||
              "Please try again! We’re continuously improving the system."
          );
        }
      } catch (e) {
        console.error("T&C accept failed:", e);
        setTncError("Please try again! We couldn’t accept the terms right now.");
      } finally {
        setTncLoading(false);
      }
      return;
    }

    setShouldRedirect(true);
  };

  if (loading || authLoading) {
    return (
      <div className="flex justify-center items-center h-screen pt-24 px-4">
        <div className="text-center">
          <TailSpin
            height="80"
            width="80"
            color="#FBC02D"
            ariaLabel="loading"
            radius="1"
            visible={true}
          />
          <p className="text-gray-600 mt-4">
            {loading ? "Checking restaurant status..." : "Authenticating..."}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen pt-24 px-4">
        <div className="text-center max-w-md">
          <div className="mb-8">
            <svg className="mx-auto h-20 w-20 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-700 mb-6">We are Closed!</h2>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#FBC02D] text-white px-8 py-4 rounded-lg hover:bg-[#F9A825] transition-colors text-lg font-semibold"
          >
            Check Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen flex flex-col justify-center items-center pt-24 px-4">
      <div className="max-w-md w-full text-center">
        <h1 className="text-3xl font-bold text-[#171725] mb-8">
          Tavahot welcomes you at {restaurantName}
        </h1>
        {transientUser !== true && (
          <p className="text-xl text-gray-600 mb-4">
            Taking you to menu in {countdown} {countdown === 1 ? 'second' : 'seconds'}...
          </p>
        )}
        <button
          onClick={handleGoToMenu}
          disabled={tncLoading}
          className={`bg-[#FBC02D] text-white px-8 py-4 rounded-lg transition-colors ${
            tncLoading ? "opacity-70 cursor-not-allowed" : "hover:bg-[#F9A825]"
          }`}
        >
          {tncLoading ? "Please wait..." : "Go to Menu"}
        </button>
        <p className="text-sm text-gray-500 mt-4">
          By tapping, I accept the{" "}
          <a
            href={TAVAHOT_TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-gray-700"
          >
            terms of service
          </a>{" "}
          &{" "}
          <a
            href={TAVAHOT_PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-gray-700"
          >
            privacy policy
          </a>
          .
        </p>
        {tncError && (
          <p className="text-sm text-red-600 mt-3">{tncError}</p>
        )}
      </div>
    </div>
  );
};

export default WelcomeWalkinCustomer;